import React, { useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface UsePrintablePagesOptions<T> {
    /**
     * Flat list of items to distribute across pages.
     * Should be a stable reference (e.g. from useMemo) to avoid unnecessary re-pagination.
     */
    items: T[];

    /**
     * Maximum height in CSS pixels available for rows on a single page.
     * Calculate as: (pageHeightMm - reservedMm) * (96 / 25.4)
     * where reservedMm accounts for headers, footers, table thead, etc.
     */
    availableHeightPx: number;

    /**
     * Renders a single measurement `<tr>` for the given item.
     * Must use the same CSS classes as the real table rows so the measured
     * heights match what will actually render on the page.
     */
    renderMeasureRow: (item: T, index: number) => React.ReactNode;

    /**
     * CSS class applied to the hidden measurement `<table>`.
     * Pass the same class used on the real table (e.g. for border-collapse).
     */
    tableClassName?: string;

    /**
     * Width of the hidden measurement container.
     * Must match the usable content width inside a pv__paper page.
     * Defaults to '174mm' (210mm − 18mm left − 18mm right padding).
     */
    measureWidth?: string;
}

export interface UsePrintablePagesResult<T> {
    /**
     * Items grouped into pages based on measured row heights.
     * Empty array on first render until the layout effect has run.
     */
    pages: T[][];

    /**
     * A React portal containing the hidden off-screen measurement table.
     * Place `{measurePortal}` anywhere inside the component's JSX return —
     * it is portaled to `document.body` so layout position doesn't matter.
     */
    measurePortal: React.ReactNode;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function usePrintablePages<T>({
    items,
    availableHeightPx,
    renderMeasureRow,
    tableClassName,
    measureWidth = '174mm',
}: UsePrintablePagesOptions<T>): UsePrintablePagesResult<T> {
    const measureRef = useRef<HTMLTableSectionElement>(null);
    const [pages, setPages] = useState<T[][]>([]);

    useLayoutEffect(() => {
        if (!items.length) {
            setPages([]);
            return;
        }
        if (!measureRef.current) {
            setPages([items]);
            return;
        }

        const trs = Array.from(measureRef.current.querySelectorAll('tr'));
        if (!trs.length) {
            setPages([items]);
            return;
        }

        const result: T[][] = [];
        let page: T[] = [];
        let used = 0;

        items.forEach((item, i) => {
            const rowH = trs[i]?.getBoundingClientRect().height ?? 0;
            if (page.length > 0 && used + rowH > availableHeightPx) {
                result.push(page);
                page = [item];
                used = rowH;
            } else {
                page.push(item);
                used += rowH;
            }
        });

        if (page.length > 0) result.push(page);
        setPages(result);
        // renderMeasureRow is intentionally omitted — it only affects portal rendering,
        // not pagination logic, and is stable for a given set of items.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, availableHeightPx]);

    const measurePortal = ReactDOM.createPortal(
        React.createElement(
            'div',
            {
                style: {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: measureWidth,
                    visibility: 'hidden',
                    pointerEvents: 'none',
                    zIndex: -1,
                },
            },
            React.createElement(
                'table',
                { className: tableClassName, style: { width: '100%', borderCollapse: 'collapse' } },
                React.createElement(
                    'tbody',
                    { ref: measureRef },
                    items.map((item, i) => renderMeasureRow(item, i))
                )
            )
        ),
        document.body
    );

    return { pages, measurePortal };
}

